import { useEffect, useRef, useState } from "react";
import { CircularProgress, Typography } from "src/components";
import * as d3 from "d3";

import worldGeo from "src/data/countries.json";
import logoRect from "src/assets/logo-rect.png";

const pastelPalette = [
  "#A8DADC",
  "#F6BD60", 
  "#F28482",
  "#84A59D",
  "#F5CAC3",
  "#B8C0FF",
  "#CDB4DB",
  "#FFDDD2", 
  "#90BE6D",
  "#FFD6A5"
];

// Americas country codes (ISO 3166-1 Alpha-2)
const americasCountries = [
  'US', 'CA', 'MX', 'BR', 'AR', 'CL', 'PE', 'CO', 'VE', 'EC', 'BO', 'PY', 'UY', 'GY', 'SR', 'FK',
  'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'CU', 'JM', 'HT', 'DO', 'PR', 'TT', 'BB', 'GD', 'VC', 
  'LC', 'DM', 'AG', 'KN', 'BS', 'PM'
];

export default function StatisticsMap({ countriesVisited = [] }) {
    const svgRef = useRef(null);
    const [geoData, setGeoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        try {
            setError(false);
            // Filter to only Americas countries
            const americasFeatures = worldGeo.features.filter(feature => {
                const iso = feature.properties.ISO_A2;
                return americasCountries.includes(iso);
            });

            setGeoData({
                type: "FeatureCollection",
                features: americasFeatures
            });
        } catch (err) {
            console.error("Error loading geo data:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!geoData) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const width = 800;
        const height = 500;

        // Create projection focused on Americas
        const projection = d3.geoMercator()
            .center([-80, -15])  // Center on Americas
            .scale(450)
            .translate([width / 1.8, height / 1.4]);

        const path = d3.geoPath().projection(projection);

        // Create main group
        const g = svg.append("g");

        // Draw countries
        geoData.features.forEach(feature => {

            const countryCode = feature.properties.ISO_A2;
            const colorIndex = americasCountries.indexOf(countryCode);
            const baseColor = pastelPalette[colorIndex % pastelPalette.length];

            const isVisited = countriesVisited.includes(countryCode);

            g.append("path")
                .datum(feature)
                .attr("d", path)
                .attr("fill", baseColor)
                .attr("stroke", "#666")
                .attr("stroke-width", 0.6)
                .attr("opacity", isVisited ? 1 : 0.3);
        });

    }, [geoData, countriesVisited]);

    if (loading || error) {
        return (
        <div style={{
            width: "100%",
            height: "350px",
            borderRadius: "8px",
            border: "1px solid #D3D3D3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
            fontSize: "14px",
            color: "#666"
        }}>
            {loading ? 
                <CircularProgress size={22} weight={4} />
                : <Typography>Failed to load map</Typography>
            }
        </div>
        );
    }

    return (
        <div
            style={{
                position: "relative",      
                width: "100%",
                height: "400px",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #D3D3D3",
                backgroundColor: "#f0f0f0",  
            }}>

            {/* Logo in bottom-left */}
            <img
                src={logoRect}
                alt="logo"
                style={{
                    position: "absolute",
                    bottom: "40px",
                    left: "30px",
                    width: "100px",  
                    opacity: 0.7,   
                    zIndex: 2
                }}
            />

            {/* SVG map */}
            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox="0 0 800 500"
                style={{ position: "relative", zIndex: 1 }}
            />
        </div>
    );
}